import { Typography, type TypographyProps, type TypographyVariant } from '@/components/atoms/Typography';
import { ChevronDownIcon } from '@/components/atoms/icons/ChevronDownIcon';
import { useI18nContext, useUIColors } from '@/core/hooks';
import React from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';

export interface FormPickerOption {
    id: string;
    label: string;
    key?: string;
}

export interface FormPickerProps {
    label?: string;
    placeholder?: string;
    selectedOption?: FormPickerOption | null | undefined;
    onPress: () => void;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    /** Web create passes headline/primary; native defaults to subhead/primary when omitted. */
    labelVariant?: TypographyVariant;
    labelColor?: TypographyProps['color'];
    emptyColor?: TypographyProps['color'];
    /** When the option menu is open (rotates chevron; web mat-select active border). */
    isMenuOpen?: boolean;
    /** Web desktop: menu opens above the trigger (squares off top corners). */
    menuOpensUpward?: boolean;
}

function resolveDefaultLabelVariant(labelVariant?: TypographyVariant): TypographyVariant {
    if (labelVariant) {
        return labelVariant;
    }
    if (Platform.OS === 'web') {
        return 'caption';
    }
    return 'subhead';
}

function resolveDefaultLabelColor(
    labelColor?: TypographyProps['color'],
): TypographyProps['color'] {
    if (labelColor) {
        return labelColor;
    }
    if (Platform.OS === 'web') {
        return 'tertiary';
    }
    return 'primary';
}

function resolvePickerBorderColor(params: {
    hasError: boolean;
    isMenuOpen: boolean;
    outlineError: string;
    outlineActive: string;
    outlineInactive: string;
}): string {
    const { hasError, isMenuOpen, outlineError, outlineActive, outlineInactive } = params;
    if (hasError) {
        return outlineError;
    }
    if (Platform.OS !== 'web') {
        return 'transparent';
    }
    if (isMenuOpen) {
        return outlineActive;
    }
    return outlineInactive;
}

function resolveWebTextAlign(isRTL: boolean): 'left' | 'right' {
    if (Platform.OS === 'web' && isRTL) {
        return 'right';
    }
    return 'left';
}

function resolvePickerBackground(backgroundLevel1: string, backgroundLevel2: string): string {
    if (Platform.OS === 'web') {
        return backgroundLevel1;
    }
    return backgroundLevel2;
}

interface FormPickerTriggerProps {
    disabled: boolean;
    isMenuOpen: boolean;
    menuOpensUpward: boolean;
    isRTL: boolean;
    textAlign: 'left' | 'right';
    displayLabel: string;
    valueColor: TypographyProps['color'];
    borderColor: string;
    backgroundColor: string;
    iconColor: string;
    onPress: () => void;
}

function FormPickerTrigger({
    disabled,
    isMenuOpen,
    menuOpensUpward,
    isRTL,
    textAlign,
    displayLabel,
    valueColor,
    borderColor,
    backgroundColor,
    iconColor,
    onPress,
}: Readonly<FormPickerTriggerProps>) {
    const webOpenDown = Platform.OS === 'web' && isMenuOpen && !menuOpensUpward;
    const webOpenUp = Platform.OS === 'web' && isMenuOpen && menuOpensUpward;

    return (
        <Pressable
            style={[
                styles.picker,
                {
                    backgroundColor,
                    borderColor,
                    opacity: disabled ? 0.6 : 1,
                },
                webOpenDown && styles.pickerOpenDown,
                webOpenUp && styles.pickerOpenUp,
            ]}
            onPress={onPress}
            disabled={disabled}
            accessibilityState={{ expanded: isMenuOpen }}
        >
            <Typography
                variant="body"
                color={valueColor}
                style={[styles.pickerText, { textAlign }]}
            >
                {displayLabel}
            </Typography>
            <View
                style={{
                    transform: [
                        { scaleX: isRTL ? -1 : 1 },
                        { rotate: isMenuOpen ? '180deg' : '0deg' },
                    ],
                }}
            >
                <ChevronDownIcon color={iconColor} />
            </View>
        </Pressable>
    );
}

export const FormPicker = React.forwardRef<View, FormPickerProps>(
function FormPicker(
    {
    label,
    placeholder,
    selectedOption,
    onPress,
    error,
    required = false,
    disabled = false,
    labelVariant,
    labelColor,
    emptyColor,
    isMenuOpen = false,
    menuOpensUpward = false,
    },
    ref,
) {
    const colors = useUIColors();
    const { isRTL } = useI18nContext();

    const resolvedLabelVariant = resolveDefaultLabelVariant(labelVariant);
    const resolvedLabelColor = resolveDefaultLabelColor(labelColor);
    const resolvedEmptyColor = emptyColor ?? 'tertiary';
    const textAlign = resolveWebTextAlign(isRTL);

    const pickerBorderColor = resolvePickerBorderColor({
        hasError: Boolean(error),
        isMenuOpen,
        outlineError: colors.outlineError,
        outlineActive: colors.outlineActive,
        outlineInactive: colors.outlineInactive,
    });

    const displayLabel = selectedOption?.label || placeholder || '';
    const valueColor = selectedOption ? 'primary' : resolvedEmptyColor;
    const pickerBackground = resolvePickerBackground(
        colors.backgroundLevel1,
        colors.backgroundLevel2,
    );

    return (
        <View style={styles.container}>
            {label && (
                <Typography
                    variant={resolvedLabelVariant}
                    color={resolvedLabelColor}
                    style={[styles.label, { textAlign }]}
                >
                    {label}
                    {required && ' *'}
                </Typography>
            )}
            <View ref={ref} collapsable={false} style={styles.triggerHost}>
                <FormPickerTrigger
                    disabled={disabled}
                    isMenuOpen={isMenuOpen}
                    menuOpensUpward={menuOpensUpward}
                    isRTL={isRTL}
                    textAlign={textAlign}
                    displayLabel={displayLabel}
                    valueColor={valueColor}
                    borderColor={pickerBorderColor}
                    backgroundColor={pickerBackground}
                    iconColor={colors.fontPrimary}
                    onPress={onPress}
                />
            </View>
            {!!error && (
                <Typography variant="footnote" color="error" style={styles.error}>
                    {error}
                </Typography>
            )}
        </View>
    );
});

FormPicker.displayName = 'FormPicker';

const styles = StyleSheet.create({
    container: {
        gap: 8,
    },
    label: {
        // Typography component handles font styling
    },
    triggerHost: {
        width: '100%',
        alignSelf: 'stretch',
    },
    picker: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'stretch',
        width: '100%',
        borderRadius: 8,
        borderWidth: 1,
        paddingHorizontal: 16,
        // Native: 12; Web: 16 (pre-merge web + native padding split)
        paddingVertical: Platform.OS === 'web' ? 16 : 12,
        minHeight: 48,
        gap: 10,
    },
    pickerOpenDown: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        zIndex: 3,
    },
    pickerOpenUp: {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        zIndex: 3,
    },
    pickerText: {
        flex: 1,
        // Typography component handles RTL text alignment automatically
    },
    error: {
        // Typography component handles font styling
    },
});
